// =============================================================================
// FILE: packages/renderer/src/components/security/VerifyDocumentModal.tsx
// PURPOSE: Public document-authentication check. Enter an R2 fingerprint code
//   (e.g. A7F9-23KD-8821) to verify a deliverable's origin + license status.
// =============================================================================

import { useState, type JSX } from 'react';
import { X, ShieldCheck, ShieldX, Search, Loader2 } from 'lucide-react';
import { verifyFingerprint } from '../../lib/security.js';

export function VerifyDocumentModal({ onClose }: { onClose: () => void }): JSX.Element {
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState<Awaited<ReturnType<typeof verifyFingerprint>> | null>(null);

  const run = async () => {
    if (!code.trim()) return;
    setBusy(true); setRes(null);
    try { setRes(await verifyFingerprint(code)); } catch { setRes({ valid: false, code }); } finally { setBusy(false); }
  };

  return (
    <div data-testid="verify-document-modal" className="fixed inset-0 z-[10002] grid place-items-center p-6" style={{ background: 'rgba(4,7,14,0.9)', backdropFilter: 'blur(8px)' }}>
      <div className="relative w-full max-w-md rounded-2xl overflow-hidden" style={{ background: 'rgba(9,13,22,0.98)', border: '1px solid rgba(94,234,212,0.35)' }}>
        <div className="flex items-center justify-between px-6 py-4" style={{ borderBottom: '1px solid rgba(94,234,212,0.2)' }}>
          <div className="flex items-center gap-2 text-sm font-bold text-zinc-100"><ShieldCheck className="h-4 w-4 text-teal-300" /> Verify R2 Document</div>
          <button data-testid="verify-close" onClick={onClose} className="h-8 w-8 grid place-items-center rounded-md text-zinc-500 hover:text-zinc-200" style={{ border: '1px solid rgba(63,63,70,0.6)' }}><X className="h-4 w-4" /></button>
        </div>
        <div className="p-6 space-y-4">
          <p className="text-[12px] text-zinc-400">Enter the authentication code printed on any R2 deliverable to confirm it is a genuine, licensed document.</p>
          <div className="flex gap-2">
            <input data-testid="verify-code" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="A7F9-23KD-8821"
              className="flex-1 px-3 py-2 rounded-lg text-[14px] font-mono tracking-widest text-zinc-100 outline-none" style={{ background: 'rgba(2,6,12,0.6)', border: '1px solid rgba(148,163,184,0.25)' }} />
            <button data-testid="verify-run" onClick={run} disabled={busy} className="px-4 rounded-lg text-[12px] font-bold flex items-center gap-1.5" style={{ background: '#2dd4bf', color: '#04121b' }}>
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
            </button>
          </div>
          {res && (
            res.valid ? (
              <div data-testid="verify-valid" className="rounded-xl p-4" style={{ background: 'rgba(52,211,153,0.08)', border: '1px solid rgba(52,211,153,0.3)' }}>
                <div className="flex items-center gap-2 text-emerald-400 font-bold text-[13px] mb-2"><ShieldCheck className="h-4 w-4" /> Genuine R2 Document</div>
                <div className="text-[12px] text-zinc-300 space-y-1">
                  <div>Product: <span className="text-zinc-100">{res.product_name || '—'}</span></div>
                  <div>Project: <span className="text-zinc-100">{res.project_name || res.project_id}</span></div>
                  <div>License: <span className="font-mono text-teal-300">{res.license_id}</span> · <span className="text-emerald-400">{res.license_status}</span></div>
                  <div>Issued to: <span className="text-zinc-100">{res.customer}</span></div>
                  <div>Issued: <span className="text-zinc-100">{res.issued_at ? new Date(res.issued_at).toLocaleString() : '—'}</span></div>
                </div>
              </div>
            ) : (
              <div data-testid="verify-invalid" className="rounded-xl p-4 flex items-center gap-2 text-red-300" style={{ background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.3)' }}>
                <ShieldX className="h-4 w-4" /> <span className="text-[13px] font-semibold">No record found — this code is not a recognized R2 document.</span>
              </div>
            )
          )}
        </div>
      </div>
    </div>
  );
}
